<template>
    <v-menu
        v-model="menu"
        :close-on-content-click="false"
        location="bottom end"
        transition="slide-y-transition"
    >
        <template v-slot:activator="{ props }">
            <v-btn
                icon
                variant="text"
                v-bind="props"
                v-tooltip.bottom-center="$t('cart.name')"
                class="cart-btn"
            >
                <v-badge
                    :content="count"
                    :model-value="count > 0"
                    color="accent"
                    offset-x="-5"
                    offset-y="-5"
                    class="cart-badge"
                >
                    <v-icon>mdi-cart</v-icon>
                </v-badge>
            </v-btn>
        </template>

            <v-card class="cart-card">
                <v-list>
                    <v-list-item>
                        <template #prepend>
                            <v-avatar>
                                <v-icon color="primary">mdi-cart</v-icon>
                            </v-avatar>
                        </template>

                        <v-list-item-title>{{ $t("cart.name") }}</v-list-item-title>

                        <template #append>
                            <v-chip
                                label
                                class="d-flex justify-center mr-2"
                            >{{ $t("cart.part-count", count) }}
                            </v-chip>
                            <v-btn
                                icon
                                size="small"
                                variant="text"
                                @click="menu = false"
                                v-tooltip.bottom-end="$t('cart.hide')"
                            >
                                <v-icon size="small">mdi-arrow-up-drop-circle-outline</v-icon>
                            </v-btn>
                        </template>
                    </v-list-item>
                </v-list>

                <v-divider></v-divider>
                <div class="cart-list">
                    <v-list>
                        <v-form v-model="partListValid">
                            <v-list-item
                                v-for="(item, index) in cartItems"
                                :key="item.partNumber"
                                :class="{ nobg: true, highlight: highlight == index }"
                            >
                                <v-col>
                                    <v-row>
                                        <div class="mr-2">
                                            <QuantityChip
                                                v-model="item.quantity"
                                                :min="1"
                                                :max="99"
                                                @input="update(item)"
                                            ></QuantityChip>
                                        </div>

                                        <div class="flex-grow-1 pb-0 cart-item-text">
                                            <v-list-item-title>{{ item.partName }}</v-list-item-title>
                                            <v-list-item-subtitle>{{ item.partNumber }}</v-list-item-subtitle>
                                        </div>

                                        <div class="d-flex cart-item-actions">
                                            <v-btn
                                                icon
                                                size="small"
                                                variant="plain"
                                                v-if="!item.note"
                                                @click="toggleComment(item.id)"
                                                v-tooltip.bottom-end="$t('cart.add-comment')"
                                            >
                                                <v-icon size="small">mdi-comment-edit-outline</v-icon>
                                            </v-btn>
                                            <v-btn
                                                icon
                                                size="small"
                                                variant="plain"
                                                @click="remove(item.id)"
                                                v-tooltip.bottom-end="$t('cart.remove')"
                                            >
                                                <v-icon size="small">mdi-close</v-icon>
                                            </v-btn>
                                        </div>
                                    </v-row>
                                    <v-row v-if="item.note || showComments[item.id]" class="mb-1">
                                        <v-textarea
                                            :model-value="item.note"
                                            @update:modelValue="v => { item.note = v || null; }"
                                            @click:clear="item.note = null; update(item)"
                                            @blur="() => { if (!item.note) { item.note = null; } update(item) }"
                                            :placeholder="$t('cart.type-comment')"
                                            class="text-body-2 mx-2"
                                            :rules="[r.maxLength(200)]"
                                            density="compact"
                                            hide-details="auto"
                                            no-resize
                                            auto-grow
                                            clearable
                                            clear-icon="mdi-backspace-outline"
                                            rows="1"
                                        ></v-textarea>
                                    </v-row>
                                </v-col>
                            </v-list-item>
                        </v-form>
                        <v-list-item class="mt-2 px-2">
                            <v-sheet
                                v-if="!!manualPart"
                                color="#f8f8f8"
                                border
                                class="pa-2"
                                rounded
                            >
                                <v-form v-model="manualPartValid">
                                    <v-col>
                                        <v-row class="d-flex flex-row align-center">
                                            <div class="align-self-center mr-2">
                                                <QuantityChip
                                                    v-model="manualPart.quantity"
                                                    :min="1"
                                                    :max="99"
                                                ></QuantityChip>
                                            </div>

                                            <div class="flex-grow-1 pb-1 pt-0">
                                                <v-text-field
                                                    v-model="manualPart.partName"
                                                    :rules="[r.required, r.maxLength(30)]"
                                                    :placeholder="$t('cart.part-name')"
                                                    class="text-body-2"
                                                    variant="filled"
                                                    density="compact"
                                                    hide-details="auto"
                                                ></v-text-field>
                                                <v-text-field
                                                    v-model="manualPart.partNumber"
                                                    :rules="[r.required, r.maxLength(30)]"
                                                    :placeholder="$t('cart.part-number')"
                                                    class="text-body-2"
                                                    variant="filled"
                                                    density="compact"
                                                    hide-details="auto"
                                                ></v-text-field>
                                            </div>

                                            <div class="align-self-center d-flex flex-column">
                                                <v-btn
                                                    icon
                                                    @click="saveManualPart"
                                                    v-tooltip.bottom-end="$t('cart.confirm')"
                                                    color="success"
                                                    :disabled="!manualPartValid"
                                                    size="small"
                                                >
                                                    <v-icon size="small">mdi-check-circle</v-icon>
                                                </v-btn>
                                                <v-btn
                                                    icon
                                                    size="small"
                                                    @click="manualPart = null"
                                                    v-tooltip.bottom-end="$t('cart.cancel')"
                                                    class="my-1"
                                                >
                                                    <v-icon size="small">mdi-close</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-row>
                                        <v-row>
                                            <v-textarea
                                                v-model="manualPart.note"
                                                variant="filled"
                                                :placeholder="$t('cart.optional-comment')"
                                                class="text-body-2"
                                                :rules="[r.maxLength(200)]"
                                                density="compact"
                                                hide-details="auto"
                                                no-resize
                                                auto-grow
                                                clearable
                                                clear-icon="mdi-backspace-outline"
                                                rows="1"
                                            ></v-textarea>
                                        </v-row>
                                    </v-col>
                                </v-form>
                            </v-sheet>
                            <v-btn
                                v-else
                                variant="outlined"
                                block
                                size="large"
                                @click="manualPart = { manual: true, quantity: 1, partName: '', partNumber: '', note: '' }"
                            >
                                <v-icon class="mr-2">mdi-plus-circle-outline</v-icon>
                                {{ $t("cart.add-manually") }}
                            </v-btn>
                        </v-list-item>
                    </v-list>
                </div>
                <v-card-actions>
                    <OrderButton class="pa-0" :disabled="!count || !partListValid || !!manualPart"></OrderButton>
                </v-card-actions>
            </v-card>
        </v-menu>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { nextTick } from 'vue';
import QuantityChip from '@/venit/components/cortona-venit/components/basic/QuantityChip.vue';
import rules from '@/venit/components/cortona-venit/utils/rules';
import { useCortonaViewerStore } from '@/stores/cortonaViewer';
import OrderButton from '../orders/OrderButton.vue';

const showComments = ref<Record<string, boolean>>({});
const menu = ref(false);
const highlight = ref(-1);
const partListValid = ref(true);
const manualPart = ref<any>(null);
const manualPartValid = ref(false);
const r = rules;

const cortonaViewerStore = useCortonaViewerStore();

const cartItems = computed(() => cortonaViewerStore.cartItems);
const count = computed(() => cartItems.value.length);

const update = (item: any) => cortonaViewerStore.updateItem(item);


const add = async (item: any) => {
    try {
        let existing = await cortonaViewerStore.addToCart(item);
        if (existing >= 0) {
            menu.value = true;
            highlight.value = existing;
            nextTick(() => setTimeout(() => (highlight.value = -1), 200));
        }
    } catch {
        // Error already shown as toast by api.js checkResponse
    }
};

const remove = (id: any) => cortonaViewerStore.removeFromCart(id);

const saveManualPart = () => {
    if (!manualPart.value.note) manualPart.value.note = undefined;
    cortonaViewerStore.addToCart(manualPart.value);
    manualPart.value = null;
};

const toggleComment = (itemId: any) => {
    showComments.value[itemId] = !showComments.value[itemId];
};

cortonaViewerStore.loadCart();

defineExpose({ add });
</script>

<style lang="scss">
.cart-badge .v-badge__badge {
    font-size: 10px;
    font-weight: 600;
    min-width: 16px;
    height: 16px;
    padding: 2px 4px;
}

.highlight {
    background-color: rgba(var(--v-theme-primary), 0.2) !important;
}

.cart-card {
    width: 480px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;

    @media screen and (max-width: 768px) {
        width: 100vw;
        max-width: 100vw;
    }
}

.cart-list {
    flex: 1 1 auto;
    overflow-y: auto;
    max-height: 60vh;
}

.cart-card .v-list-item {
    padding: 8px 16px;
}

.cart-card .v-row {
    margin: 0;
    align-items: center;
    flex-wrap: nowrap;
}

.cart-item-text {
    min-width: 0;
    overflow: hidden;
}

.cart-card .v-col {
    padding: 4px;
}

.nobg {
    background-color: white;
    transition: background-color 5s ease-in-out;
}

.cart-item-actions .v-btn {
    opacity: 0.5;
    transition: opacity 0.2s, background-color 0.2s;
}

.cart-item-actions .v-btn:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.08);
}
</style>